/** Domain empty rules: NoClient / NoProject are sentinel rows (`useAsDefault`), not null. Task may be null. */

export interface DefaultEntityRef {
	readonly id?: string;
	readonly name?: string | null;
	readonly useAsDefault?: boolean;
}

export const isStoredNoClientName = (name: string | null | undefined): boolean => {
	const trimmed = name?.trim() ?? "";
	return trimmed.length === 0 || /^no client$/i.test(trimmed);
};

export const isStoredNoProjectName = (name: string | null | undefined): boolean => {
	const trimmed = name?.trim() ?? "";
	return trimmed.length === 0 || /^no project$/i.test(trimmed);
};

export const isNoClient = (client: DefaultEntityRef | null | undefined, noClientId?: string | null): boolean => {
	if (!client) {
		return true;
	}
	if (client.useAsDefault === true) {
		return true;
	}
	if (noClientId && client.id === noClientId) {
		return true;
	}
	// Nested refs often omit useAsDefault until the clients store has loaded.
	if (!noClientId && isStoredNoClientName(client.name)) {
		return true;
	}
	return false;
};

export const displayClientName = (
	name: string | null | undefined,
	noClientLabel: string,
	client?: DefaultEntityRef | null,
	noClientId?: string | null
): string => {
	if (isNoClient(client ?? { name }, noClientId)) {
		return noClientLabel;
	}
	const trimmed = name?.trim() ?? "";
	return trimmed || noClientLabel;
};

export const isNoProject = (project: DefaultEntityRef | null | undefined, noProjectId?: string | null): boolean => {
	if (!project) {
		return true;
	}
	if (project.useAsDefault === true) {
		return true;
	}
	if (noProjectId && project.id === noProjectId) {
		return true;
	}
	if (!noProjectId && isStoredNoProjectName(project.name)) {
		return true;
	}
	return false;
};

/** Task has no sentinel; empty means null/undefined only. */
export const isTaskEmpty = (task: { readonly id?: string } | null | undefined): boolean => task == null || !task.id;

export const findNoClientId = (clients: readonly DefaultEntityRef[]): string | null =>
	clients.find((client) => client.useAsDefault === true)?.id ??
	clients.find((client) => Boolean(client.id) && /^no client$/i.test(client.name?.trim() ?? ""))?.id ??
	null;

export const findNoProjectId = (projects: readonly DefaultEntityRef[]): string | null =>
	projects.find((project) => project.useAsDefault === true)?.id ??
	projects.find((project) => Boolean(project.id) && /^no project$/i.test(project.name?.trim() ?? ""))?.id ??
	null;
